import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format, isAfter, addDays } from 'date-fns';
import {
  getITAssets, getITAsset, createITAsset, updateITAsset, deleteITAsset,
  assignITAsset, returnITAsset, closeITAsset,
  createAssetServiceLog, deleteAssetServiceLog, deleteAssetAttachment,
  getStaff, uploadsUrl, api,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import { exportToExcel, fmtDate as fmtDateExp, exportAmount } from '../utils/exportExcel';
import DateRangeFilter, { inDateRange } from '../components/DateRangeFilter';

// ─── Types ────────────────────────────────────────────────────────────────────

type AssetStatus = 'IN_STOCK' | 'ASSIGNED' | 'UNDER_REPAIR' | 'DISPOSED' | 'LOST' | 'SOLD';
// Mirrors the backend ITAssetCategory enum exactly — these two lists used to
// drift (the UI offered categories like DESKTOP/KEYBOARD/UPS that the
// database enum didn't have, so saving one failed).
type AssetCategory =
  | 'COMPUTER' | 'LAPTOP' | 'MONITOR' | 'PRINTER' | 'PHONE' | 'TABLET'
  | 'SERVER' | 'NETWORK_DEVICE' | 'SOFTWARE_LICENSE' | 'FURNITURE' | 'OTHER';

const ASSET_CATEGORIES: AssetCategory[] = [
  'COMPUTER', 'LAPTOP', 'MONITOR', 'PRINTER', 'PHONE', 'TABLET',
  'SERVER', 'NETWORK_DEVICE', 'SOFTWARE_LICENSE', 'FURNITURE', 'OTHER',
];
const categoryLabel = (c: string) => c.replace('_', ' ').replace(/\w\S*/g, (w) => w[0] + w.slice(1).toLowerCase());

interface Staff { id: number; staffName: string; }
interface Accessory { id: number; name: string; notes?: string; }
interface Assignment {
  id: number;
  staff: Staff;
  assignedAt: string;
  returnedAt?: string | null;
  notes?: string | null;
}
type ServiceType = 'REPAIR' | 'REPLACEMENT';
interface ServiceLog {
  id: number;
  type: ServiceType;
  partName: string;
  serviceDate: string;
  expenseIncurred?: number;
  warrantyExpiry?: string;
  comments?: string;
}
interface AssetAttachment {
  id: number;
  docType: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
}
interface LinkedTicket {
  id: number;
  ticketNumber: string;
  title: string;
  status: string;
  priority: string;
  createdAt: string;
}
interface ITAsset {
  id: number;
  assetCode: string;
  name: string;
  category: AssetCategory;
  brand?: string;
  model?: string;
  serialNumber?: string;
  status: AssetStatus;
  assignedTo?: Staff;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpiry?: string;
  location?: string;
  notes?: string;
  endDate?: string;
  endAmount?: number;
  createdAt: string;
  accessories?: Accessory[];
  assignments?: Assignment[];
  serviceLogs?: ServiceLog[];
  attachments?: AssetAttachment[];
  tickets?: LinkedTicket[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusBadge = (s: AssetStatus): string => {
  switch (s) {
    case 'IN_STOCK':     return 'bg-green-100 text-green-700';
    case 'ASSIGNED':     return 'bg-blue-100 text-blue-700';
    case 'UNDER_REPAIR': return 'bg-yellow-100 text-yellow-700';
    case 'DISPOSED':
    case 'LOST':         return 'bg-red-100 text-red-700';
    case 'SOLD':         return 'bg-gray-200 text-gray-600';
    default:             return 'bg-gray-100 text-gray-600';
  }
};

const warrantyClass = (expiry?: string): string => {
  if (!expiry) return 'text-gray-400';
  const exp = new Date(expiry);
  const now = new Date();
  if (!isAfter(exp, now)) return 'text-red-600 font-semibold'; // expired
  if (!isAfter(exp, addDays(now, 30))) return 'text-orange-500 font-semibold'; // within 30 days
  return 'text-gray-600';
};

const fmtDate = (d?: string) => d ? format(new Date(d), 'dd-MMM-yy') : '—';
const fmtMoney = (n?: number) =>
  n != null ? '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—';
const fileSizeStr = (b: number) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const isClosed = (s: AssetStatus) => s === 'SOLD' || s === 'DISPOSED';

// ─── Empty form ───────────────────────────────────────────────────────────────

const emptyForm = () => ({
  name: '',
  category: 'LAPTOP' as AssetCategory,
  brand: '',
  model: '',
  serialNumber: '',
  purchaseDate: '',
  purchasePrice: '',
  warrantyExpiry: '',
  location: '',
  notes: '',
  accessories: '', // comma-separated in the form, split into an array on submit
});

// ─── Component ────────────────────────────────────────────────────────────────

const ITAssets: React.FC = () => {
  const { isAdmin } = useAuth();
  const [assets, setAssets] = useState<ITAsset[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [warrantyFrom, setWarrantyFrom] = useState('');
  const [warrantyTo, setWarrantyTo] = useState('');
  const [sortField, setSortField] = useState<'assetCode' | 'name' | 'category' | 'status' | 'warrantyExpiry'>('assetCode');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Add/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<ITAsset | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  // Guards the asset create/edit submit against a fast repeat click — see
  // Tasks.tsx certSaving/taskSaving for the same pattern.
  const [assetSaving, setAssetSaving] = useState(false);

  // Assign modal
  const [assignAsset, setAssignAsset] = useState<ITAsset | null>(null);
  const [assigneeId, setAssigneeId] = useState('');

  // Close (sold/disposed) modal
  const [closeAsset, setCloseAsset] = useState<ITAsset | null>(null);
  const [closeForm, setCloseForm] = useState({ endStatus: 'DISPOSED' as 'SOLD' | 'DISPOSED', endDate: '', endAmount: '' });

  // Detail drawer
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ITAsset | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([getITAssets(), getStaff()]);
      setAssets(aRes.data || []);
      setStaff(sRes.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const res = await getITAsset(id);
      setDetail(res.data);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { if (detailId != null) loadDetail(detailId); }, [detailId, loadDetail]);

  const refreshAfterDetailChange = async () => {
    if (detailId != null) await loadDetail(detailId);
    fetchAll();
  };

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = assets.filter((a) => {
    if (filterCategory && a.category !== filterCategory) return false;
    if (filterStatus && a.status !== filterStatus) return false;
    if (filterAssignee) {
      if (!a.assignedTo || String(a.assignedTo.id) !== filterAssignee) return false;
    }
    if (assetSearch) {
      const q = assetSearch.toLowerCase();
      if (!a.name.toLowerCase().includes(q) && !a.assetCode.toLowerCase().includes(q) &&
          !(a.brand || '').toLowerCase().includes(q) && !(a.serialNumber || '').toLowerCase().includes(q)) return false;
    }
    if ((warrantyFrom || warrantyTo) && !inDateRange(a.warrantyExpiry, warrantyFrom, warrantyTo)) return false;
    return true;
  });

  const handleSort = (f: typeof sortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setPage(1);
  };
  const SortIcon = ({ f }: { f: typeof sortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    let av = '', bv = '';
    if (sortField === 'assetCode') { av = a.assetCode; bv = b.assetCode; }
    else if (sortField === 'name') { av = a.name; bv = b.name; }
    else if (sortField === 'category') { av = a.category; bv = b.category; }
    else if (sortField === 'status') { av = a.status; bv = b.status; }
    else if (sortField === 'warrantyExpiry') { av = a.warrantyExpiry || '9999'; bv = b.warrantyExpiry || '9999'; }
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  }), [filtered, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pagedAssets = sorted.slice((page - 1) * pageSize, page * pageSize);

  // ── Add/Edit ───────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingAsset(null);
    setForm(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (a: ITAsset) => {
    setEditingAsset(a);
    setForm({
      name: a.name,
      category: a.category,
      brand: a.brand || '',
      model: a.model || '',
      serialNumber: a.serialNumber || '',
      purchaseDate: a.purchaseDate ? a.purchaseDate.split('T')[0] : '',
      purchasePrice: a.purchasePrice != null ? String(a.purchasePrice) : '',
      warrantyExpiry: a.warrantyExpiry ? a.warrantyExpiry.split('T')[0] : '',
      location: a.location || '',
      notes: a.notes || '',
      accessories: (a.accessories || []).map((x) => x.name).join(', '),
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (assetSaving) return;
    setFormError('');
    const payload = {
      ...form,
      purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : undefined,
      purchaseDate: form.purchaseDate || undefined,
      warrantyExpiry: form.warrantyExpiry || undefined,
      accessories: form.accessories.split(',').map((s) => s.trim()).filter(Boolean),
    };
    setAssetSaving(true);
    try {
      if (editingAsset) await updateITAsset(editingAsset.id, payload);
      else await createITAsset(payload);
      setShowModal(false);
      if (editingAsset && detailId === editingAsset.id) loadDetail(editingAsset.id);
      fetchAll();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Error saving asset');
    } finally {
      setAssetSaving(false);
    }
  };

  const handleDelete = async (a: ITAsset) => {
    if (!window.confirm(`Delete asset "${a.assetCode} — ${a.name}"? This also removes its history, service log and documents.`)) return;
    try { await deleteITAsset(a.id); if (detailId === a.id) setDetailId(null); fetchAll(); }
    catch (err: any) { alert(err.response?.data?.message || 'Cannot delete'); }
  };

  // ── Assign / Return / Close ────────────────────────────────────────────────

  const handleAssign = async () => {
    if (!assignAsset || !assigneeId) return;
    try {
      await assignITAsset(assignAsset.id, Number(assigneeId));
      const wasDetail = detailId === assignAsset.id;
      setAssignAsset(null);
      setAssigneeId('');
      if (wasDetail) loadDetail(assignAsset.id);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error assigning asset');
    }
  };

  const handleReturn = async (a: ITAsset) => {
    if (!window.confirm(`Return "${a.name}" from ${a.assignedTo?.staffName}?`)) return;
    try { await returnITAsset(a.id); if (detailId === a.id) loadDetail(a.id); fetchAll(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error returning asset'); }
  };

  const openClose = (a: ITAsset) => {
    setCloseAsset(a);
    setCloseForm({ endStatus: 'DISPOSED', endDate: format(new Date(), 'yyyy-MM-dd'), endAmount: '' });
  };

  const handleClose = async () => {
    if (!closeAsset) return;
    try {
      await closeITAsset(closeAsset.id, {
        endStatus: closeForm.endStatus,
        endDate: closeForm.endDate || undefined,
        endAmount: closeForm.endStatus === 'SOLD' && closeForm.endAmount ? Number(closeForm.endAmount) : undefined,
      });
      const wasDetail = detailId === closeAsset.id;
      setCloseAsset(null);
      if (wasDetail) loadDetail(closeAsset.id);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error closing asset');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <p className="text-gray-400 text-sm py-8 text-center">Loading IT Assets...</p>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">IT Asset Management</h2>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary text-sm"
            onClick={() => exportToExcel(
              filtered.map((a) => ({
                ID: a.id,
                'Asset Code': a.assetCode,
                Name: a.name,
                Category: a.category,
                Brand: a.brand || '',
                Model: a.model || '',
                'Serial Number': a.serialNumber || '',
                Status: a.status.replace('_', ' '),
                'Assigned To': a.assignedTo?.staffName || '',
                'Purchase Date': fmtDateExp(a.purchaseDate),
                'Purchase Price': exportAmount(a.purchasePrice),
                'Warranty Expiry': fmtDateExp(a.warrantyExpiry),
                Location: a.location || '',
                Accessories: (a.accessories || []).map((x) => x.name).join(', '),
                Notes: a.notes || '',
              })),
              'IT Assets',
              'IT_Assets',
            )}
          >
            ⬇ Export Excel
          </button>
          <button className="btn-primary" onClick={openCreate}>+ Add Asset</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4">
          <input className="input-field max-w-[180px]" placeholder="Search name, code, serial…" value={assetSearch}
            onChange={(e) => { setAssetSearch(e.target.value); setPage(1); }} />
          <select className="input-field max-w-[160px]" value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {ASSET_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
          <select className="input-field max-w-[160px]" value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="IN_STOCK">In Stock</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="UNDER_REPAIR">Under Repair</option>
            <option value="LOST">Lost</option>
            <option value="SOLD">Sold</option>
            <option value="DISPOSED">Disposed</option>
          </select>
          <select className="input-field max-w-[200px]" value={filterAssignee}
            onChange={(e) => { setFilterAssignee(e.target.value); setPage(1); }}>
            <option value="">All Assignees</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
          </select>
          <span className="text-sm text-gray-400 self-center">{filtered.length} asset{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('assetCode')}>Asset Code <SortIcon f="assetCode" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('name')}>Name <SortIcon f="name" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('category')}>Category <SortIcon f="category" /></th>
                <th className="table-header">Brand / Model</th>
                <th className="table-header">Serial #</th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('status')}>Status <SortIcon f="status" /></th>
                <th className="table-header">Assigned To</th>
                <th className="table-header">
                  <DateRangeFilter
                    label="Warranty Expiry"
                    fromValue={warrantyFrom}
                    toValue={warrantyTo}
                    onChange={(f, t) => { setWarrantyFrom(f); setWarrantyTo(t); setPage(1); }}
                    sortable
                    onSort={() => handleSort('warrantyExpiry')}
                    sortIcon={<SortIcon f="warrantyExpiry" />}
                  />
                </th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedAssets.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="table-cell font-mono font-medium text-blue-700 text-xs cursor-pointer" onClick={() => setDetailId(a.id)}>{a.assetCode}</td>
                  <td className="table-cell font-medium cursor-pointer" onClick={() => setDetailId(a.id)}>{a.name}</td>
                  <td className="table-cell text-xs text-gray-600">{categoryLabel(a.category)}</td>
                  <td className="table-cell text-xs text-gray-500">
                    {[a.brand, a.model].filter(Boolean).join(' / ') || '—'}
                  </td>
                  <td className="table-cell text-xs font-mono text-gray-500">{a.serialNumber || '—'}</td>
                  <td className="table-cell">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge(a.status)}`}>
                      {a.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="table-cell text-xs text-gray-500">{a.assignedTo?.staffName || '—'}</td>
                  <td className={`table-cell text-xs ${warrantyClass(a.warrantyExpiry)}`}>
                    {fmtDate(a.warrantyExpiry)}
                  </td>
                  <td className="table-cell">
                    <div className="flex gap-2 flex-wrap">
                      <button className="text-gray-600 hover:text-gray-900 text-xs font-medium" onClick={() => setDetailId(a.id)}>View</button>
                      {!isClosed(a.status) && (
                        <>
                          <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => openEdit(a)}>Edit</button>
                          {a.status !== 'ASSIGNED' && (
                            <button className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                              onClick={() => { setAssignAsset(a); setAssigneeId(''); }}>
                              Assign
                            </button>
                          )}
                          {a.status === 'ASSIGNED' && (
                            <button className="text-orange-600 hover:text-orange-800 text-xs font-medium"
                              onClick={() => handleReturn(a)}>
                              Return
                            </button>
                          )}
                          <button className="text-gray-500 hover:text-gray-700 text-xs font-medium" onClick={() => openClose(a)}>Close</button>
                        </>
                      )}
                      {isAdmin && (
                        <button className="text-red-600 hover:text-red-800 text-xs font-medium"
                          onClick={() => handleDelete(a)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="table-cell text-center text-gray-400 py-8">
                    No assets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Rows:</span>
                <select className="border rounded px-2 py-1 text-sm" value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                <span className="px-3 py-1 text-sm">{page} / {totalPages}</span>
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingAsset ? `Edit Asset — ${editingAsset.assetCode}` : 'Add IT Asset'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="label">Name *</label>
                  <input className="input-field" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="label">Category *</label>
                  <select className="input-field" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value as AssetCategory })}>
                    {ASSET_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Brand</label>
                  <input className="input-field" value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                </div>
                <div>
                  <label className="label">Model</label>
                  <input className="input-field" value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })} />
                </div>
                <div>
                  <label className="label">Serial Number</label>
                  <input className="input-field font-mono" value={form.serialNumber}
                    onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} />
                </div>
                <div>
                  <label className="label">Purchase Date</label>
                  <input type="date" className="input-field" value={form.purchaseDate}
                    onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })} />
                </div>
                <div>
                  <label className="label">Purchase Price (₹)</label>
                  <input type="number" min="0" step="0.01" className="input-field" value={form.purchasePrice}
                    onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })} />
                </div>
                <div>
                  <label className="label">Warranty Expiry</label>
                  <input type="date" className="input-field" value={form.warrantyExpiry}
                    onChange={(e) => setForm({ ...form, warrantyExpiry: e.target.value })} />
                </div>
                <div>
                  <label className="label">Location</label>
                  <input className="input-field" placeholder="e.g. Server Room, Floor 2" value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="label">Accessories</label>
                  <input className="input-field" placeholder="Bag, Charger, Keyboard, Mouse (comma-separated)" value={form.accessories}
                    onChange={(e) => setForm({ ...form, accessories: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="label">Notes</label>
                  <textarea className="input-field" rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>

              {formError && <p className="text-red-600 text-sm">{formError}</p>}

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" className="btn-secondary" disabled={assetSaving} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={assetSaving}>{assetSaving ? 'Saving…' : editingAsset ? 'Update' : 'Add Asset'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {assignAsset && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Assign Asset</h3>
            <p className="text-sm text-gray-500 mb-4">
              {assignAsset.assetCode} — {assignAsset.name}
              {assignAsset.assignedTo && <span className="block text-xs mt-1">Currently with {assignAsset.assignedTo.staffName} — will be transferred.</span>}
            </p>
            <label className="label">Assign To *</label>
            <select className="input-field mb-4" value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}>
              <option value="">Select staff member...</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
            </select>
            <div className="flex gap-3 justify-end">
              <button className="btn-secondary" onClick={() => setAssignAsset(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleAssign} disabled={!assigneeId}>Assign</button>
            </div>
          </div>
        </div>
      )}

      {/* Close (Sold / Disposed) Modal */}
      {closeAsset && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Close Asset</h3>
            <p className="text-sm text-gray-500 mb-4">{closeAsset.assetCode} — {closeAsset.name}</p>
            <label className="label">End Status *</label>
            <select className="input-field mb-3" value={closeForm.endStatus}
              onChange={(e) => setCloseForm({ ...closeForm, endStatus: e.target.value as 'SOLD' | 'DISPOSED' })}>
              <option value="DISPOSED">Disposed</option>
              <option value="SOLD">Sold</option>
            </select>
            <label className="label">Date *</label>
            <input type="date" className="input-field mb-3" value={closeForm.endDate}
              onChange={(e) => setCloseForm({ ...closeForm, endDate: e.target.value })} />
            {closeForm.endStatus === 'SOLD' && (
              <>
                <label className="label">Amount Realised (₹)</label>
                <input type="number" min="0" step="0.01" className="input-field mb-3" value={closeForm.endAmount}
                  onChange={(e) => setCloseForm({ ...closeForm, endAmount: e.target.value })} />
              </>
            )}
            <p className="text-xs text-gray-400 mb-4">This returns the asset from its current holder and marks it closed. It can't be assigned again afterwards.</p>
            <div className="flex gap-3 justify-end">
              <button className="btn-secondary" onClick={() => setCloseAsset(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleClose}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {detailId != null && (
        <AssetDetailDrawer
          detail={detail}
          loading={detailLoading}
          onClose={() => { setDetailId(null); setDetail(null); }}
          onChanged={refreshAfterDetailChange}
        />
      )}
    </div>
  );
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────

const SERVICE_TYPES: ServiceType[] = ['REPAIR', 'REPLACEMENT'];
const DOC_TYPES = ['INVOICE', 'WARRANTY', 'OTHER'];

const emptyServiceForm = () => ({
  type: 'REPAIR' as ServiceType,
  partName: '',
  serviceDate: format(new Date(), 'yyyy-MM-dd'),
  expenseIncurred: '',
  warrantyExpiry: '',
  comments: '',
});

const AssetDetailDrawer: React.FC<{
  detail: ITAsset | null;
  loading: boolean;
  onClose: () => void;
  onChanged: () => void;
}> = ({ detail, loading, onClose, onChanged }) => {
  const [tab, setTab] = useState<'overview' | 'history' | 'service' | 'docs' | 'tickets'>('overview');
  const [serviceForm, setServiceForm] = useState(emptyServiceForm());
  const [serviceSaving, setServiceSaving] = useState(false);
  const [docType, setDocType] = useState('INVOICE');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleAddServiceLog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail || serviceSaving) return;
    if (!serviceForm.partName || !serviceForm.serviceDate) return;
    setServiceSaving(true);
    try {
      await createAssetServiceLog(detail.id, {
        ...serviceForm,
        expenseIncurred: serviceForm.expenseIncurred ? Number(serviceForm.expenseIncurred) : undefined,
        warrantyExpiry: serviceForm.warrantyExpiry || undefined,
      });
      setServiceForm(emptyServiceForm());
      onChanged();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error adding service log entry');
    } finally {
      setServiceSaving(false);
    }
  };

  const handleDeleteServiceLog = async (id: number) => {
    if (!window.confirm('Delete this service log entry?')) return;
    try { await deleteAssetServiceLog(id); onChanged(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error deleting entry'); }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !detail) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('docType', docType);
      await api.post(`/it-assets/${detail.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChanged();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error uploading document');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (id: number) => {
    if (!window.confirm('Remove this document?')) return;
    try { await deleteAssetAttachment(id); onChanged(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error removing document'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50">
      <div className="bg-white h-full w-full max-w-2xl shadow-xl overflow-y-auto p-6">
        {loading || !detail ? (
          <p className="text-gray-400 text-sm py-8 text-center">Loading…</p>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{detail.assetCode} — {detail.name}</h3>
                <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge(detail.status)}`}>
                  {detail.status.replace('_', ' ')}
                </span>
              </div>
              <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={onClose}>×</button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b mb-4 text-sm">
              {([
                ['overview', 'Overview'],
                ['history', `Possession History (${detail.assignments?.length || 0})`],
                ['service', `Service Log (${detail.serviceLogs?.length || 0})`],
                ['docs', `Documents (${detail.attachments?.length || 0})`],
                ['tickets', `Linked Tickets (${detail.tickets?.length || 0})`],
              ] as const).map(([key, label]) => (
                <button key={key}
                  className={`px-3 py-2 -mb-px border-b-2 ${tab === key ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setTab(key)}>
                  {label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-gray-400">Category:</span> {categoryLabel(detail.category)}</div>
                  <div><span className="text-gray-400">Brand / Model:</span> {[detail.brand, detail.model].filter(Boolean).join(' / ') || '—'}</div>
                  <div><span className="text-gray-400">Serial #:</span> {detail.serialNumber || '—'}</div>
                  <div><span className="text-gray-400">Location:</span> {detail.location || '—'}</div>
                  <div><span className="text-gray-400">Purchase Date:</span> {fmtDate(detail.purchaseDate)}</div>
                  <div><span className="text-gray-400">Purchase Price:</span> {fmtMoney(detail.purchasePrice)}</div>
                  <div className={warrantyClass(detail.warrantyExpiry)}><span className="text-gray-400">Warranty Expiry:</span> {fmtDate(detail.warrantyExpiry)}</div>
                  <div><span className="text-gray-400">Assigned To:</span> {detail.assignedTo?.staffName || '—'}</div>
                  {isClosed(detail.status) && (
                    <>
                      <div><span className="text-gray-400">{detail.status === 'SOLD' ? 'Sold On' : 'Disposed On'}:</span> {fmtDate(detail.endDate)}</div>
                      {detail.status === 'SOLD' && <div><span className="text-gray-400">Amount Realised:</span> {fmtMoney(detail.endAmount)}</div>}
                    </>
                  )}
                </div>
                {detail.notes && (
                  <div><span className="text-gray-400">Notes:</span> <span className="whitespace-pre-wrap">{detail.notes}</span></div>
                )}
                <div>
                  <span className="text-gray-400">Accessories:</span>{' '}
                  {detail.accessories && detail.accessories.length > 0 ? (
                    <span className="inline-flex flex-wrap gap-1 mt-1">
                      {detail.accessories.map((acc) => (
                        <span key={acc.id} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">{acc.name}</span>
                      ))}
                    </span>
                  ) : ' —'}
                </div>
              </div>
            )}

            {tab === 'history' && (
              <div className="space-y-2">
                {detail.assignments && detail.assignments.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="table-header">Staff</th>
                        <th className="table-header">Assigned</th>
                        <th className="table-header">Returned</th>
                        <th className="table-header">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.assignments.map((asg) => (
                        <tr key={asg.id}>
                          <td className="table-cell">{asg.staff?.staffName || '—'}</td>
                          <td className="table-cell text-xs">{fmtDate(asg.assignedAt)}</td>
                          <td className="table-cell text-xs">
                            {asg.returnedAt ? fmtDate(asg.returnedAt) : <span className="text-blue-600 font-medium">Current</span>}
                          </td>
                          <td className="table-cell text-xs text-gray-500">{asg.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="text-gray-400 text-sm">No possession history yet.</p>}
              </div>
            )}

            {tab === 'service' && (
              <div className="space-y-4">
                {!isClosed(detail.status) && (
                  <form onSubmit={handleAddServiceLog} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                    <div className="grid grid-cols-2 gap-2">
                      <select className="input-field text-sm" value={serviceForm.type}
                        onChange={(e) => setServiceForm({ ...serviceForm, type: e.target.value as ServiceType })}>
                        {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t === 'REPAIR' ? 'Repair' : 'Replacement'}</option>)}
                      </select>
                      <input className="input-field text-sm" placeholder="Part name *" required value={serviceForm.partName}
                        onChange={(e) => setServiceForm({ ...serviceForm, partName: e.target.value })} />
                      <input type="date" className="input-field text-sm" required value={serviceForm.serviceDate}
                        onChange={(e) => setServiceForm({ ...serviceForm, serviceDate: e.target.value })} />
                      <input type="number" min="0" step="0.01" className="input-field text-sm" placeholder="Expense incurred (₹)" value={serviceForm.expenseIncurred}
                        onChange={(e) => setServiceForm({ ...serviceForm, expenseIncurred: e.target.value })} />
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500">Warranty on this part (if any)</label>
                        <input type="date" className="input-field text-sm" value={serviceForm.warrantyExpiry}
                          onChange={(e) => setServiceForm({ ...serviceForm, warrantyExpiry: e.target.value })} />
                      </div>
                      <textarea className="input-field text-sm col-span-2" rows={2} placeholder="Comments" value={serviceForm.comments}
                        onChange={(e) => setServiceForm({ ...serviceForm, comments: e.target.value })} />
                    </div>
                    <div className="flex justify-end">
                      <button type="submit" className="btn-primary text-sm" disabled={serviceSaving}>{serviceSaving ? 'Saving…' : '+ Add Entry'}</button>
                    </div>
                  </form>
                )}
                {detail.serviceLogs && detail.serviceLogs.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="table-header">Type</th>
                        <th className="table-header">Part</th>
                        <th className="table-header">Date</th>
                        <th className="table-header">Expense</th>
                        <th className="table-header">Part Warranty</th>
                        <th className="table-header">Comments</th>
                        <th className="table-header"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.serviceLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="table-cell text-xs">{log.type === 'REPAIR' ? 'Repair' : 'Replacement'}</td>
                          <td className="table-cell text-sm">{log.partName}</td>
                          <td className="table-cell text-xs">{fmtDate(log.serviceDate)}</td>
                          <td className="table-cell text-xs">{fmtMoney(log.expenseIncurred)}</td>
                          <td className={`table-cell text-xs ${warrantyClass(log.warrantyExpiry)}`}>{fmtDate(log.warrantyExpiry)}</td>
                          <td className="table-cell text-xs text-gray-500">{log.comments || '—'}</td>
                          <td className="table-cell">
                            <button className="text-red-500 hover:text-red-700 text-xs" onClick={() => handleDeleteServiceLog(log.id)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="text-gray-400 text-sm">No repairs or replacements logged.</p>}
              </div>
            )}

            {tab === 'docs' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <select className="input-field text-sm max-w-[140px]" value={docType} onChange={(e) => setDocType(e.target.value)}>
                    {DOC_TYPES.map((d) => <option key={d} value={d}>{d.charAt(0) + d.slice(1).toLowerCase()}</option>)}
                  </select>
                  <input ref={fileRef} type="file" className="hidden" id="asset-doc-upload" onChange={handleUpload}
                    accept=".jpg,.jpeg,.png,.gif,.pdf" />
                  <label htmlFor="asset-doc-upload" className={`btn-secondary text-xs cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
                    {uploading ? 'Uploading…' : '+ Upload Document'}
                  </label>
                </div>
                {detail.attachments && detail.attachments.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {detail.attachments.map((a) => (
                      <div key={a.id} className="border border-gray-200 rounded-lg p-2 text-center">
                        {a.mimeType.startsWith('image') ? (
                          <a href={uploadsUrl(a.fileName)} target="_blank" rel="noreferrer">
                            <img src={uploadsUrl(a.fileName)} alt={a.originalName} className="w-full h-24 object-cover rounded" />
                          </a>
                        ) : (
                          <a href={uploadsUrl(a.fileName)} target="_blank" rel="noreferrer"
                            className="block h-24 flex items-center justify-center bg-gray-50 rounded">
                            <span className="text-3xl">📄</span>
                          </a>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{a.docType}</p>
                        <p className="text-xs text-gray-600 truncate" title={a.originalName}>{a.originalName}</p>
                        <p className="text-xs text-gray-400">{fileSizeStr(a.fileSize)}</p>
                        <button className="text-red-500 hover:text-red-700 text-xs" onClick={() => handleDeleteAttachment(a.id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-gray-400 text-sm">No documents uploaded — add the purchase invoice or warranty card here.</p>}
              </div>
            )}

            {tab === 'tickets' && (
              <div className="space-y-2">
                {detail.tickets && detail.tickets.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="table-header">Ticket #</th>
                        <th className="table-header">Title</th>
                        <th className="table-header">Status</th>
                        <th className="table-header">Priority</th>
                        <th className="table-header">Raised</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.tickets.map((t) => (
                        <tr key={t.id}>
                          <td className="table-cell font-mono text-xs text-blue-700">{t.ticketNumber}</td>
                          <td className="table-cell text-sm">{t.title}</td>
                          <td className="table-cell text-xs">{t.status.replace('_', ' ')}</td>
                          <td className="table-cell text-xs">{t.priority}</td>
                          <td className="table-cell text-xs">{fmtDate(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="text-gray-400 text-sm">No tickets have been raised against this asset. Link one from the ticket form's "Related Asset" field.</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ITAssets;
